import { Component } from '@theme/component';

/** A progressive enhancement: the colour links always remain native links. */
class ZentusColourRail extends Component {
  // All rails update in one shared frame: every layout read runs before any write, so a grid of
  // cards costs two layouts per frame instead of two per card.
  static #pending = new Set();
  static #frame = 0;

  #controller;
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#click, { signal });
    this.addEventListener('keydown', this.#keydown, { signal });
    this.addEventListener('focusin', this.#focus, { signal });
    this.addEventListener('scroll', this.#schedule, { capture: true, passive: true, signal });
    this.#observe();
  }

  // Horizon calls this after morphing a retained card during filtering/pagination.
  updatedCallback() {
    super.updatedCallback();
    if (this.isConnected) this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    ZentusColourRail.#pending.delete(this);
  }

  #observe() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#schedule);
    this.#resizeObserver.observe(this);
    const viewport = this.querySelector('.z-card-colours');
    if (viewport) {
      this.#resizeObserver.observe(viewport);
      for (const link of viewport.children) this.#resizeObserver.observe(link);
    }
    this.#schedule();
  }

  #schedule = () => {
    ZentusColourRail.#pending.add(this);
    ZentusColourRail.#frame ||= requestAnimationFrame(ZentusColourRail.#flush);
  };

  static #flush = () => {
    ZentusColourRail.#frame = 0;
    const rails = [...ZentusColourRail.#pending].filter((rail) => rail.isConnected);
    ZentusColourRail.#pending.clear();
    const states = rails.map((rail) => rail.#measure()).filter(Boolean);
    for (const { previous, next, overflow } of states) previous.hidden = next.hidden = !overflow;
    // Showing the controls can narrow the viewport, so read the scroll range after toggling them.
    for (const state of states) {
      state.maximum = Math.max(0, state.viewport.scrollWidth - state.viewport.clientWidth);
      state.position = Math.abs(state.viewport.scrollLeft);
    }
    for (const { previous, next, maximum, position } of states) {
      previous.disabled = position <= 1;
      next.disabled = position >= maximum - 1;
    }
  };

  #measure() {
    const viewport = this.querySelector('.z-card-colours');
    const previous = this.querySelector('[data-rail-previous]');
    const next = this.querySelector('[data-rail-next]');
    if (!viewport || !previous || !next) return null;
    // Compare with the full rail width so showing the controls cannot create overflow.
    return { viewport, previous, next, overflow: viewport.scrollWidth > this.clientWidth + 1 };
  }

  #behavior() {
    return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  }

  #click = (event) => {
    const button = event.target.closest('[data-rail-previous], [data-rail-next]');
    if (!button || button.disabled) return;
    const viewport = this.querySelector('.z-card-colours');
    if (!viewport) return;
    const direction = button.hasAttribute('data-rail-previous') ? -1 : 1;
    const rtl = getComputedStyle(viewport).direction === 'rtl' ? -1 : 1;
    viewport.scrollBy({ left: direction * rtl * Math.max(48, viewport.clientWidth * .8), behavior: this.#behavior() });
  };

  #focus = (event) => {
    const link = event.target.closest('.z-colour-link');
    const viewport = this.querySelector('.z-card-colours');
    if (!link || !viewport) return;
    const item = link.getBoundingClientRect();
    const frame = viewport.getBoundingClientRect();
    const left = item.left < frame.left ? item.left - frame.left : item.right > frame.right ? item.right - frame.right : 0;
    if (left) viewport.scrollBy({ left, behavior: 'instant' });
    this.#schedule();
  };

  #keydown = (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const link = event.target.closest('.z-colour-link');
    if (!link) return;
    const links = [...this.querySelectorAll('.z-colour-link')];
    const index = links.indexOf(link);
    const rtl = getComputedStyle(link).direction === 'rtl';
    let target;
    if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = links.length - 1;
    else if (event.key === 'ArrowRight') target = index + (rtl ? -1 : 1);
    else if (event.key === 'ArrowLeft') target = index + (rtl ? 1 : -1);
    else return;
    event.preventDefault();
    links[Math.max(0, Math.min(target, links.length - 1))]?.focus({ preventScroll: true });
  };
}

if (!customElements.get('zentus-colour-rail')) customElements.define('zentus-colour-rail', ZentusColourRail);
